// ─────────────────────────────────────────────────────────────
// Logger Service — Structured logging to Firebase Realtime DB
// ─────────────────────────────────────────────────────────────
import { ref, push, set, serverTimestamp } from 'firebase/database';
import { db }                              from '@/firebase';
import { getState }                        from '@/store/tripState';

// ── Log types ─────────────────────────────────────────────────
export type LogLevel  = 'error' | 'warn' | 'info';
export type LogAction =
  | 'login' | 'logout' | 'login_failed'
  | 'page_view'
  | 'trip_created' | 'trip_updated' | 'trip_deleted'
  | 'traveler_added' | 'traveler_removed'
  | 'password_changed' | 'password_change_failed'
  | 'user_created' | 'user_promoted_admin'
  | 'doc_uploaded' | 'attachment_uploaded'
  | 'invite_created' | 'invite_revoked' | 'invite_used'
  | 'permission_denied' | 'firebase_error' | 'unknown_error';

interface LogEntry {
  level:      LogLevel;
  action:     LogAction;
  uid:        string | null;
  email:      string | null;
  message:    string;
  meta?:      Record<string, unknown>;
  userAgent:  string;
  timestamp:  ReturnType<typeof serverTimestamp>;
}

// ── Internal write — never throws (logging must not break the app) ──
async function writeLog(
  bucket:  'errors' | 'actions' | 'access',
  entry:   Omit<LogEntry, 'uid' | 'email' | 'userAgent' | 'timestamp'>
): Promise<void> {
  try {
    const { currentUser } = getState();
    const record: LogEntry = {
      ...entry,
      uid:       currentUser?.uid  ?? null,
      email:     currentUser?.email ?? null,
      userAgent: navigator.userAgent.slice(0, 200),
      timestamp: serverTimestamp(),
    };
    // Use push() to auto-generate a key
    const logRef = push(ref(db, `logs/${bucket}`));
    await set(logRef, record);

    // Update admin stats counter
    await updateCounter(entry.action);
  } catch {
    // Silently ignore — logging must never crash the app
    console.warn('[Logger] Failed to write log');
  }
}

// ── Update aggregated counters for dashboard ──────────────────
async function updateCounter(action: LogAction): Promise<void> {
  try {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const { runTransaction } = await import('firebase/database');
    const countRef = ref(db, `adminStats/daily/${today}/${action}`);
    await runTransaction(countRef, (current: number | null) => (current ?? 0) + 1);
    // Also update total counter
    const totalRef = ref(db, `adminStats/totals/${action}`);
    await runTransaction(totalRef, (current: number | null) => (current ?? 0) + 1);
  } catch {
    // Ignore counter failures
  }
}

// ── Public API ────────────────────────────────────────────────

/** Log an important user action */
export function logAction(
  action:  LogAction,
  message: string,
  meta?:   Record<string, unknown>
): void {
  writeLog('actions', { level: 'info', action, message, meta });
}

let lastPageView = { signature: '', timestamp: 0 };

/** Record an authenticated screen or tab visit without logging URL parameters. */
export function logPageView(
  page: string,
  meta?: Record<string, unknown>
): void {
  const { currentUser, currentTripId } = getState();
  if (!currentUser) return;

  // Navigation callbacks can fire twice for the same history event. Avoid only
  // these technical duplicates; a later return to the page is logged normally.
  const signature = `${currentUser.uid}|${currentTripId ?? ''}|${page}`;
  const now = Date.now();
  if (lastPageView.signature === signature && now - lastPageView.timestamp < 750) return;
  lastPageView = { signature, timestamp: now };

  writeLog('access', {
    level: 'info',
    action: 'page_view',
    message: `Acessou: ${page}`,
    meta: {
      page,
      tripId: currentTripId,
      pathname: window.location.pathname,
      ...meta,
    },
  });
}

/** Log an error with optional context */
export function logError(
  action:  LogAction,
  error:   unknown,
  meta?:   Record<string, unknown>
): void {
  const message = error instanceof Error
    ? error.message
    : String(error);
  const stack = error instanceof Error ? error.stack?.slice(0, 500) : undefined;
  writeLog('errors', {
    level:   'error',
    action,
    message,
    meta:    { ...meta, stack },
  });
}

/** Log a warning */
export function logWarn(
  action:  LogAction,
  message: string,
  meta?:   Record<string, unknown>
): void {
  writeLog('errors', { level: 'warn', action, message, meta });
}

/** Log Firebase permission denied errors */
export function logPermissionDenied(path: string, operation: string): void {
  writeLog('errors', {
    level:   'error',
    action:  'permission_denied',
    message: `Permission denied: ${operation} at ${path}`,
    meta:    { path, operation },
  });
}
